import React, { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Input, SliderCheckbox, SpinnerOverlay, SubmissionButtons } from '@components/common';

import { useModal } from '@components/common/Modal/utils';

import { IPresetFormTypes } from './PresetForm.types';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import {
	IS_SHARED_PRESET_LABEL,
	TOOLTIP_SHARED_PRESET_INFO,
	TOOLTIP_SHARED_PRESET_IRREVERSIBLE,
} from '@constants/KPI/kpi-common';
import { EFormFields } from '@components/common/ModalComponents/PresetForm/constants';

import { schema } from './schema';

import styles from './PresetNameForm.module.scss';

const PresetNameForm = ({
	editablePreset,
	presetSaveDate,
	onEdit,
	onSave,
	setIsDirty,
	closePresetNameForm,
	setPresetValue,
	presetValue,
	isLoading,
	isOranVendor,
}: IPresetFormTypes) => {
	const isEditMode = !presetSaveDate;
	const isDisabledSharedCheckbox = isEditMode && editablePreset?.isShared;

	const { onClose } = useModal();

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		setError,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			name: editablePreset?.name || '',
			isShared: presetSaveDate?.isShared || editablePreset?.isShared,
		},
	});

	useEffect(() => {
		setIsDirty(isDirty);
	}, [isDirty, setIsDirty]);

	const onSubmit = (values: FieldValues) => {
		const setNameError = (message: string) => setError('name', { message });

		if (isEditMode && editablePreset) {
			onEdit({
				data: {
					name: values.name,
					id: editablePreset.id,
					isShared: isOranVendor ? values.isShared : undefined,
				},
				setNameError,
				closePresetForm: closePresetNameForm,
				setPresetValue,
				isPresetValue: !!presetValue,
			});
		} else if (presetSaveDate) {
			onSave({
				data: {
					...presetSaveDate,
					name: values.name,
					isShared: isOranVendor ? values.isShared : undefined,
				},
				setNameError,
				closePresetForm: closePresetNameForm,
			});
		}
	};

	return (
		<div
			className={styles.presetFormContainer}
			data-testid='preset-form-container'
		>
			{isLoading && <SpinnerOverlay />}
			<Input
				errorMessage={errors?.name?.message}
				label='Preset Name'
				name={EFormFields.name}
				placeholder='Enter preset name'
				register={register}
				required
				setValue={setValue}
			/>
			{isOranVendor && (
				<SliderCheckbox
					disabled={isDisabledSharedCheckbox}
					id={EFormFields.isShared}
					isLabelAfter
					label={IS_SHARED_PRESET_LABEL}
					name={EFormFields.isShared}
					register={register}
					tooltipText={isDisabledSharedCheckbox
						? TOOLTIP_SHARED_PRESET_IRREVERSIBLE
						: TOOLTIP_SHARED_PRESET_INFO
					}
				/>
			)}
			<SubmissionButtons
				negativeHandler={onClose}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Save'
			/>
		</div>
	);
};

export default PresetNameForm;
